// Tornozelo: componentes do score de retorno ao esporte (RTP).
//
// Toda pontuação aqui devolve `null` para teste não realizado, nunca 0. A cópia antiga do
// ConclusionService usava `Number.isFinite(Number(valor))` para validar, e como
// `Number('')` e `Number(null)` valem 0, um campo em branco virava "tempo 0 segundos" e
// recebia a nota máxima — inflando o RTP total na conclusão e na ACIA. O PDF já validava
// certo; esta é a versão correta.

const { isFilled, toNumber, round } = require('./primitives');

// Tempo menor é melhor: a primeira faixa em que o tempo couber define a nota.
const TIMED_TEST_RULES = {
    side_hop_time: [
        { under: 10, score: 4 },
        { upTo: 13, score: 2 }
    ],
    figure8_hop_time: [
        { under: 13, score: 2 },
        { upTo: 18, score: 1 }
    ]
};

function ankleSingleLegStanceScore(info, side) {
    const errors = toNumber(info[`single_leg_stance_errors_${side}`]);
    if (errors === null) return null;

    let score = 0;
    if (errors === 0) score = 2;
    else if (errors <= 3) score = 1;

    if (info[`single_leg_stance_apprehension_${side}`] === false) score += 1;
    return score;
}

// Cobre side hop e figure-8: mesma mecânica, limiares diferentes.
function ankleTimedScore(info, side, type) {
    const time = toNumber(info[`${type}_${side}`]);
    if (time === null) return null;

    const rules = TIMED_TEST_RULES[type];
    if (!rules) return null;

    let score = 0;
    for (const rule of rules) {
        const fits = rule.under !== undefined ? time < rule.under : time <= rule.upTo;
        if (fits) {
            score = rule.score;
            break;
        }
    }

    if (info[`${type.replace('_time', '')}_apprehension_${side}`] === false) score += 1;
    return score;
}

// O percentual é arredondado antes de comparar com o limiar para o número exibido no
// relatório bater com a classificação que o acompanha.
function ankleSebtScore(info, side) {
    const legSize = toNumber(info[`leg_size_${side}`]);

    // Aqui, e só aqui, tentativa em branco entra como 0 na média em vez de invalidar a
    // direção. É o comportamento que os relatórios já publicados usam; mudar deslocaria
    // scores antigos, então fica preservado até haver decisão clínica sobre o assunto.
    const directionAverage = (prefix) => {
        const values = [1, 2, 3].map((trial) => Number(info[`y_balance_${prefix}_${side}_t${trial}`]));
        if (values.some((value) => !Number.isFinite(value))) return 0;
        return round(values.reduce((total, value) => total + value, 0) / values.length);
    };

    const normalized = (value) => (legSize !== null && legSize !== 0 ? round((value / legSize) * 100) : 0);

    const ant = normalized(directionAverage('previous'));
    const pm = normalized(directionAverage('posteromedial'));
    const pl = normalized(directionAverage('posterolateral'));
    const composite = round((ant + pm + pl) / 3);

    let compositeScore = 0;
    if (composite > 95) compositeScore = 4;
    else if (composite >= 90) compositeScore = 2;

    return compositeScore
        + (ant > 60 ? 1 : 0)
        + (pm > 90 ? 1 : 0)
        + (info[`sebt_apprehension_${side}`] === false ? 1 : 0);
}

function ankleFaamScore(info, key, minimum) {
    const items = info[key];
    if (!items || typeof items !== 'object') return null;

    const values = Object.values(items).filter((value) => isFilled(value) && value !== 'na');
    if (!values.length) return null;

    const percent = round((values.reduce((total, value) => total + Number(value), 0) / (4 * values.length)) * 100);
    if (percent > 95) return 2;
    if (percent >= minimum) return 1;
    return 0;
}

function ankleAlrRsiScore(info) {
    const items = info.alr_rsi;
    if (!items || typeof items !== 'object') return null;

    const values = Object.values(items);
    if (!values.length || values.some((value) => !isFilled(value))) return null;

    const percent = round(values.reduce((total, value) => total + Number(value), 0) / 1.2);
    if (percent > 76) return 3;
    if (percent >= 63) return 2;
    if (percent >= 55) return 1;
    return 0;
}

function ankleRtpComponents(info, side) {
    const faamValues = [
        ankleFaamScore(info, 'faam_avd', 90),
        ankleFaamScore(info, 'faam_sport', 80)
    ].filter((value) => value !== null);

    return {
        sls: ankleSingleLegStanceScore(info, side),
        sebt: ankleSebtScore(info, side),
        sht: ankleTimedScore(info, side, 'side_hop_time'),
        f8t: ankleTimedScore(info, side, 'figure8_hop_time'),
        faam: faamValues.length ? faamValues.reduce((total, value) => total + value, 0) : null,
        alrRsi: ankleAlrRsiScore(info)
    };
}

// Componente não realizado soma 0 — o total é "o que o paciente pontuou até aqui".
function ankleRtpTotal(info, side) {
    const components = ankleRtpComponents(info, side);
    return Object.values(components).reduce((total, value) => total + (value || 0), 0);
}

function ankleRtpInterpretation(score) {
    if (score >= 20) return 'Alta prontidão para RTS/RTP';
    if (score >= 8) return 'Prontidão intermediária';
    return 'Baixa prontidão / alto risco';
}

module.exports = {
    ankleSingleLegStanceScore,
    ankleTimedScore,
    ankleSebtScore,
    ankleFaamScore,
    ankleAlrRsiScore,
    ankleRtpComponents,
    ankleRtpTotal,
    ankleRtpInterpretation
};
